'use client';
import React, { useEffect, useState } from 'react';

interface AchievementBadgeProps {
  score: number;
  total: number;
  passmark: number;
}

interface BadgeConfig {
  emoji: string;
  title: string;
  subtitle: string;
  color: string;
  bg: string;
  border: string;
  glow: string;
}

function getBadgeConfig(score: number, total: number, passmark: number): BadgeConfig {
  const pct = (score / total) * 100;
  if (pct >= 96) {
    return {
      emoji: '🏆',
      title: 'Highway Code Expert',
      subtitle: 'Outstanding — near-perfect score',
      color: '#FCD34D',
      bg: 'rgba(245,158,11,0.12)',
      border: 'rgba(245,158,11,0.35)',
      glow: 'rgba(245,158,11,0.25)',
    };
  }
  if (pct >= 86) {
    return {
      emoji: '🚗',
      title: 'Road Safety Specialist',
      subtitle: 'Excellent — DVSA pass standard exceeded',
      color: '#34D399',
      bg: 'rgba(16,185,129,0.12)',
      border: 'rgba(16,185,129,0.35)',
      glow: 'rgba(16,185,129,0.2)',
    };
  }
  if (score >= passmark) {
    return {
      emoji: '⭐',
      title: 'Driving Knowledge Achiever',
      subtitle: 'Good — DVSA pass mark reached',
      color: '#60A5FA',
      bg: 'rgba(59,130,246,0.12)',
      border: 'rgba(59,130,246,0.35)',
      glow: 'rgba(59,130,246,0.2)',
    };
  }
  if (pct >= 60) {
    return {
      emoji: '📚',
      title: 'Theory Test Learner',
      subtitle: 'Making progress — keep practising',
      color: '#A78BFA',
      bg: 'rgba(167,139,250,0.12)',
      border: 'rgba(167,139,250,0.35)',
      glow: 'rgba(167,139,250,0.2)',
    };
  }
  return {
    emoji: '🔄',
    title: 'Keep Practising',
    subtitle: 'Review the Highway Code and try again',
    color: '#94A3B8',
    bg: 'rgba(100,116,139,0.12)',
    border: 'rgba(100,116,139,0.3)',
    glow: 'rgba(100,116,139,0.15)',
  };
}

export default function AchievementBadge({ score, total, passmark }: AchievementBadgeProps) {
  const [visible, setVisible] = useState(false);
  const badge = getBadgeConfig(score, total, passmark);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`flex flex-col items-center gap-3 transition-all duration-500 ${visible ? 'badge-reveal' : 'opacity-0 scale-50'}`}
      aria-label={`Achievement badge: ${badge.title}`}
    >
      <div
        className="w-24 h-24 rounded-2xl flex items-center justify-center text-5xl"
        style={{
          background: badge.bg,
          border: `2px solid ${badge.border}`,
          boxShadow: `0 0 40px ${badge.glow}`,
        }}
        role="img"
        aria-label={badge.emoji}
      >
        {badge.emoji}
      </div>
      <div className="text-center">
        <div className="font-bold text-lg" style={{ color: badge.color }}>
          {badge.title}
        </div>
        <div className="text-sm text-secondary-foreground mt-0.5">{badge.subtitle}</div>
      </div>
    </div>
  );
}